<template>
    <div>
        <b-breadcrumb>
            <b-breadcrumb-item to="/">
                <i class="fa fa-home"></i>
                Home
            </b-breadcrumb-item>
            <b-breadcrumb-item to="/idts">Interdepartmental Transfers</b-breadcrumb-item>
            <b-breadcrumb-item active>{{ currentTab }} IDTs</b-breadcrumb-item>
        </b-breadcrumb>

        <div class="card">
            <div class="card-header">
                <ul class="card-header-tabs nav nav-tabs">
                    <li class="nav-item">
                        <a @click="switchTabs('Network')" class="active nav-link"
                            data-toggle="tab"
                            href="#">Network</a>
                    </li>
                    <li class="nav-item">
                        <a @click="switchTabs('Lease')" class="nav-link"
                            data-toggle="tab"
                            href="#">Lease</a>
                    </li>
                </ul>
            </div>
            <div class="card-body">
              <AppLoadingContainer :predicate="isLoading">
                <AppCardCollapsable :display-footer="true" :is-expanded="false" card-header-class="bg-secondary text-white d-flex">
                  <template v-slot:header>
                    <span>FAMIS File Information</span>
                    <span class="ml-auto">Status: {{ latestFAMISFileStatus }}
                      <button title="Refresh information" @click.stop="refreshFAMISFileInformation()" class="btn btn-sm text-white bt-secondary pt-0 pb-0"><i class="fas fa-sync"></i></button>
                    </span>
                  </template>
                  <template v-slot:body>
                    <b-row>
                      <b-col>
                        <div class="font-weight-bold">Fiscal Year</div>
                        <div class="pl-4">{{ fiscalYear }}</div>
                      </b-col>
                      <b-col>
                        <div class="font-weight-bold">Fiscal Period</div>
                        <div class="pl-4">{{ fiscalPeriodStartDate | date }} - {{ fiscalPeriodEndDate | date }}</div>
                      </b-col>
                      <b-col>
                        <div class="font-weight-bold">IDT Number</div>
                        <div class="pl-4">{{ currentIDTNumber == null ? "Not Set" : currentIDTNumber }}</div>
                      </b-col>
                      <b-col>
                        <div class="font-weight-bold">{{ currentTab }} IDT Last Updated Date</div>
                        <div v-if="lastIDTModifiedDate == null" class="pl-4">-</div>
                        <div else class="pl-4">{{ lastIDTModifiedDate | date }}</div>
                      </b-col>
                      <b-col>
                        <div class="font-weight-bold">Last Uploaded Date</div>
                        <div v-if="latestFAMISFileCreationDate == null || latestFAMISFileStatus !== 'File Uploaded to FAMIS'">Not Uploaded</div>
                        <div v-else class="pl-4">{{ latestFAMISFileCreationDate | date }}</div>
                      </b-col>
                    </b-row>
                  </template>
                  <template v-slot:footer>
                    <button class="btn btn-success text-left" :disabled="isPerformingAction || incompleteStatus" @click="isUpdateModalVisible=true">
                      Update IDT Number
                    </button>
                    <button class="btn btn-primary text-left" :disabled="isPerformingAction || incompleteStatus" @click="updatingFAMISFile = true">
                      Initiate FAMIS File Processing
                    </button>
                  </template>
                </AppCardCollapsable>
                <br>
                <AppCardCollapsable :display-footer="false" :is-expanded="true" card-header-class="bg-dark text-white d-flex">
                    <template v-slot:header>
                      <span>{{ currentTab }} IDTs</span>
                      <span class="ml-auto">Total: {{ totalAmountCharged | currency }}</span>
                    </template>
                    <template v-slot:body>
                      <!-- The collapsable filter options -->
                      <AppFormCardCollapsable :display-footer="true" @submit.prevent="onFilterSubmit()" class="mb-3">
                          <template v-slot:header>Filter</template>
                          <template v-slot:body>
                            <div class="form-row">
                              <div class="col-md-4 col-sm-6 form-group">
                                <label for="sl">SL</label>
                                <input
                                  class="form-control"
                                  placeholder="SL Number"
                                  v-model="filterOptions.sl"
                                />
                              </div>
                              <div class="col-md-4 col-sm-6 form-group">
                                <label for="sa">SA</label>
                                <input
                                  class="form-control"
                                  placeholder="SA Number"
                                  v-model="filterOptions.sa"
                                />
                              </div>
                              <div class="col-md-4 col-sm-6 form-group">
                                <label for="organizationCode">Department Code</label>
                                <input
                                  class="form-control"
                                  placeholder="Department Code"
                                  v-model="filterOptions.organizationCode"
                                />
                              </div>
                              <div class="col-md-4 col-sm-6 form-group align-self-center">
                                  <AppCheckBox v-model="filterOptions.onlyShowDataIssues">
                                      Only Show Data with Issues
                                  </AppCheckBox>
                              </div>
                            </div>
                          </template>
                          <template v-slot:footer>
                              <button :disabled="isPerformingAction" class="btn btn-primary" type="submit">Apply</button>
                          </template>
                      </AppFormCardCollapsable>
                      <div class="d-print-none dropdown mb-3 text-right">
                          <button :disabled="isPerformingAction" aria-expanded="false" aria-haspopup="true" class="btn btn-secondary dropdown-toggle"
                                  data-toggle="dropdown" style="width: 100px" type="button">
                              <span>Actions</span>
                          </button>
                          <div class="dropdown-menu">
                              <RouterLink
                                :to="{ name: 'AddIDT', params: { type: currentTab, fiscalPeriodIDTNumberId: idtNumberId } }"
                                class="btn btn-outline-light dropdown-item text-left"
                                title="Add New IDT"
                              >
                                <i class="fa fa-plus"></i>Add
                              </RouterLink>
                              <a @click.prevent="downloadIDTFrozenAndMissingAccounts()" class="btn btn-outline-light dropdown-item text-left"
                                href="javascript:void(0)" title="Export missing and frozen IDT account list to a CSV">
                                  <i class="fa fa-file-excel"></i>Download Missing and Frozen Accounts .csv
                              </a>
                          </div>
                      </div>
                      <BTable :fields="tableFields"
                              :items="idts"
                              :responsive="true"
                              primary-key="id"
                              show-empty
                              striped
                              table-class="table-align-middle">

                          <template v-slot:cell(id)="row">
                              <div class="btn-group">
                                  <router-link
                                          :to="{ name:'EditIDT', params:{ id: row.item.id, type: currentTab, accountId: row.item.accountId, organizationId: row.item.organizationId, sl: row.item.sl, sa: row.item.sa, organizationCode: row.item.organizationCode, accountIsActive: row.item.accountIsActive, accountIsFrozen: row.item.accountIsFrozen, organizationIsActive: row.item.organizationIsActive, amountCharged: row.item.amountCharged, description: row.item.description } }"
                                          class="btn btn-sm btn-warning d-print-none"
                                          data-toggle="tooltip"
                                          title="Edit IDT"
                                          :disabled="isPerformingAction">
                                      <i class="fa fa-pencil-alt"></i>Edit
                                  </router-link>
                              </div>
                          </template>

                          <template v-slot:cell(sl)="row">
                            <i v-if="row.item.accountId == null || (row.item.accountIsActive != null && !row.item.accountIsActive) || (row.item.accountIsFrozen != null && row.item.accountIsFrozen) || row.item.organizationId == null || row.item.organizationCode == null || (row.item.organizationIsActive != null && !row.item.organizationIsActive)" class="fa fa-exclamation-circle" style="color: red !important;display:inline;padding-right:5px;"></i>
                            {{ row.item.sl }}
                          </template>

                          <template v-slot:cell(amountCharged)="row">
                            {{ row.item.amountCharged | currency(true) }}
                          </template>

                          <!-- Footer Pagination -->
                          <template v-slot:custom-foot>
                              <b-tr>
                                  <b-td :colspan="tableFields.length">
                                      <AppTablePagination
                                              :page.sync="filterOptions.page"
                                              :pageSize.sync="filterOptions.pageSize"
                                              :totalCount.sync="totalIDTs"
                                              v-on:update:page="loadData()"
                                              v-on:update:pageSize="loadData()"
                                      ></AppTablePagination>
                                  </b-td>
                              </b-tr>
                          </template>
                      </BTable>
                    </template>
                </AppCardCollapsable>
              </AppLoadingContainer>
            </div>
        </div>
        <TheIDTNumberUpdateForm 
          :visible="isUpdateModalVisible" 
          :currentIDTNumber="currentIDTNumber"
          :idtNumberId="idtNumberId"
          @close="isUpdateModalVisible=false; loadData();" />
        <AppModalConfirm :visible="submittingFAMISFile" @close="submittingFAMISFile = false;" @confirm="initiateFAMISFileUploadProcess()">
            <template v-slot:header>Confirm</template>
            <AppAlertNote>
              Make sure all data is correct and an IDT number has been set before proceeding. A list of missing or frozen accounts can be exported from the {{ currentTab }} IDT actions button.
            </AppAlertNote>
            <p>Are you sure you want to generate and upload the FAMIS file for {{ currentTab }} IDTs?</p>
        </AppModalConfirm>
    </div>
</template>

<script>
    import {ToastType} from "@/services/models/shared/ToastType";
    import AppTablePagination from "../../components/shared/AppTablePagination";
    import InterdepartmentalTransfersService from "@/services/InterdepartmentalTransfersService";
    import AppCardCollapsable from "@/components/shared/AppCardCollapsable";
    import AppFormCardCollapsable from "@/components/shared/AppFormCardCollapsable";
    import {currency} from "@/filters/Currency";
    import AppCheckBox from '@/components/shared/AppCheckBox.vue';
    import AppLoadingContainer from '@/components/shared/AppLoadingContainer.vue';
    import TheIDTNumberUpdateForm from './TheIDTNumberUpdateForm.vue'
    import {date} from "@/filters/Date";
    import AppModalConfirm from "@/components/shared/AppModalConfirm";

    export default {
        components: {
            AppTablePagination,
            AppCardCollapsable,
            AppCheckBox,
            AppLoadingContainer,
            TheIDTNumberUpdateForm,
            AppFormCardCollapsable,
            AppModalConfirm
        },
        filters: {
            currency,
            date
        },
        data() {
            return {
                currentTab: 'Network',
                filterOptions: {
                    page: 1,
                    pageSize: 10,
                    sl: null,
                    sa: null,
                    organizationCode: null,
                    onlyShowDataIssues: false
                },
                isLoading: false,
                isPerformingAction: false,
                tableFields: [
                    {
                        key: 'id',
                        label: '',
                        thStyle: 'width:150px'
                    }, 
                    {
                        key: 'sl',
                        label: 'SL'
                    },
                    {
                        key: 'sa',
                        label: 'SA'
                    },
                    {
                        key: 'organizationCode',
                        label: 'Department'
                    },
                    {
                        key: 'amountCharged',
                        label: 'Amount Charged'
                    },
                    {
                        key: 'accountStatus',
                        label: "Account Status"
                    }
                ],
                idts: [],
                totalIDTs: 0,
                totalAmountCharged: 0,
                currentIDTNumber: null,
                idtNumberId: null,
                fiscalYear: null,
                fiscalPeriodStartDate: null,
                fiscalPeriodEndDate: null,
                latestFAMISFileStatus: null,
                latestFAMISFileCreationDate: null,
                isUpdateModalVisible: false,
                uploadingFAMISFile: false,
                lastIDTModifiedDate: null
            }
        },
        computed: {
          incompleteStatus() {
            return this.latestFAMISFileStatus !== 'File Generation Failed' && this.latestFAMISFileStatus !== 'File Rejected By FAMIS' && this.latestFAMISFileStatus !== 'Connection to FAMIS Failed' && this.latestFAMISFileStatus !== 'File Generation Queueing Failed' && this.latestFAMISFileStatus !== 'File Uploaded to FAMIS'
          }
        },
        methods: {
            loadData() {
              const vm = this;
              this.isLoading = true;

              InterdepartmentalTransfersService.getIDTs(this.currentTab, this.filterOptions)
                .then((responseOne) => {
                  vm.idts = responseOne.data.map((t) => {
                    return { ...t, accountStatus: t.accountId == null ? "Missing from BIMS data" : !t.accountIsActive ? "Inactive" : t.accountIsFrozen ? "Frozen" : "Good"}
                  });
                  vm.totalIDTs = responseOne.totalCount;

                  if (responseOne.data.length > 0) {
                    InterdepartmentalTransfersService.getIDTNumber(responseOne.data[0].fiscalPeriodIDTNumberId)
                      .then((responseTwo) => {
                        vm.totalAmountCharged = responseTwo.totalAmountCharged;
                        vm.currentIDTNumber = responseTwo.number;
                        vm.idtNumberId = responseTwo.id;
                        vm.fiscalYear = responseTwo.fiscalYear;
                        vm.fiscalPeriodStartDate = responseTwo.fiscalPeriodStartDate;
                        vm.fiscalPeriodEndDate = responseTwo.fiscalPeriodEndDate;
                        vm.latestFAMISFileStatus = responseTwo.latestFAMISFile.fileStatus;
                        vm.latestFAMISFileCreationDate = responseTwo.latestFAMISFile.createdDate;
                        vm.lastIDTModifiedDate = responseTwo.lastIDTModifiedDate;
                      })
                      .catch(() => {
                        vm.redirectToError();
                      })
                      .finally(() => {
                        vm.isLoading = false;
                      });
                  }
                  else {
                    vm.isLoading = false;
                  }
                })
                .catch(() => {
                  vm.redirectToError();
                });
            },
            
            onFilterSubmit() {
              this.filterOptions.page = 1;
              this.loadData();
            },

            switchTabs(type) {
              this.filterOptions.pageSize = 10;
              this.filterOptions.sl = null;
              this.filterOptions.sa = null;
              this.filterOptions.organizationCode = null;
              this.currentTab = type;

              this.onFilterSubmit();
            },

            refreshFAMISFileInformation() {
              this.isPerformingAction = true;
              const vm = this;

              InterdepartmentalTransfersService.getFAMISFileInfo(this.idtNumberId)
                .then((response) => {
                  this.latestFAMISFileStatus = response.fileStatus;
                  this.latestFAMISFileCreationDate = response.createdDate;
                })
                .catch(() => {
                  vm.alert(ToastType.Danger, "Unable to refresh FAMIS file information. Please contact NIS if the issue persists.")
                })
                .finally(() => {
                  this.isPerformingAction = false;
                });
            },

            /**
             * @description Opens up a modal to confirm uploading of famis file
             */
            onConfirmClick() {
                this.uploadingFAMISFile = true;
            },

            initiateFAMISFileUploadProcess() {
              this.isPerformingAction = true;
              const vm = this;

              InterdepartmentalTransfersService.initiateFAMISFileProcess(this.idtNumberId)
                .then(() => {
                  vm.alert(ToastType.Success, `Succesfully initiated FAMIS file processing. Check the status to make sure it has uploaded successfully`);
                })
                .catch(() => {
                  vm.alert(ToastType.Danger, "Unable to process FAMIS file. Please contact NIS if the issue persists.")
                })
                .finally(() => {
                  this.isPerformingAction = false;
                });
            },

            /**
             * @description Downloads the frozen and missing accounts list for idts CSV export to the user's browser
             */
            downloadIDTFrozenAndMissingAccounts() {
                this.isPerformingAction = true;
                const vm = this;

                InterdepartmentalTransfersService.downloadIDTFrozenAndMissingAccounts(this.currentTab)
                    .catch(() => {
                        vm.alert(ToastType.Danger, "Unable to generate report. Please contact NIS if the issue persists.")
                    })
                    .finally(() => {
                        this.isPerformingAction = false
                    });
            }
        },
        created() {
            this.loadData();
        }
    }
</script>
